import clsx from 'clsx'
import React, { HTMLAttributes, RefObject } from 'react'
import CircularProgress from '../spinners/CircularProgress'
import LazySpinner from '../spinners/LazySpinner'

type Variant =
  | 'primary'
  | 'secondary'
  | 'empty'
  | 'hollow'
  | 'cancel'
  | 'outlined'
  | 'danger'
  | 'white'

interface Props extends HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  label?: React.ReactNode
  loading?: boolean
  fullWidth?: boolean
  disabled?: boolean
  type?: 'button' | 'reset' | 'submit'
  delay?: number
  variant?: Variant
  btnRef?: RefObject<HTMLButtonElement | HTMLAnchorElement>
  href?: string // Determines if this is a link
  ariaLabel?: string // Custom accessible label
  preventNavigation?: boolean
}

const classesMap: Record<Variant, string> = {
  primary:
    'bg-jaa-teal-100 hover:bg-jaa-teal-80 disabled:bg-jaa-shades-gray-3 disabled:text-jaa-gray-2 text-jaa-shades-white',
  secondary: '',
  empty:
    'bg-jaa-shades-white text-jaa-teal-100 hover:text-jaa-teal-80 disabled:text-jaa-shades-gray-3',
  hollow:
    'h-[37px] px-4 py-1 gap-2 bg-[#CCECEF26] text-jaa-teal-100 hover:text-jaa-teal-80 disabled:text-jaa-shades-gray-3',
  cancel: 'bg-[#F7F7F7] text-jaa-shades-black sm active:scale-95',
  outlined:
    'border border-[#E2E7E9] h-8 py-1 px-6 flex justify-center items-center sm',
  danger:
    'border border-[#E55846] bg-[#E55846]  rounded text-jaa-shades-white hover:opacity-80 disabled:opacity-80 active:scale-95',
  white: 'border border-jaa-gray-300 bg-jaa-shades-white shadow-xs rounded',
}

function Button({
  label,
  className,
  loading,
  fullWidth,
  type = 'button',
  delay = 300,
  variant = 'primary',
  btnRef,
  disabled,
  href, // If href is provided, use <a>
  ariaLabel,
  onClick,
  preventNavigation,
  ...rest
}: Props) {
  const commonClasses = clsx(
    'flex sm font-semibold capitalize justify-center items-center rounded h-12 gap-2 select-none whitespace-nowrap py-1 px-6',
    classesMap[variant],
    className,
    fullWidth ? 'w-full' : 'w-auto',
    loading ? '!bg-jaa-shades-white' : '',
    disabled ? 'opacity-50 cursor-not-allowed' : ''
  )

  const accessibleLabel =
    ariaLabel || (typeof label === 'string' ? label : undefined)

  if (href) {
    return (
      <a
        ref={btnRef as RefObject<HTMLAnchorElement>}
        href={href}
        className={clsx(
          commonClasses,
          'focus:outline-none focus:ring-2 focus:ring-jaa-teal-60 '
        )}
        role='button'
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0} // Prevents focus if disabled
        aria-label={accessibleLabel}
        onClick={(e) => {
          if (disabled || preventNavigation) e.preventDefault()
          if (onClick) onClick(e)
        }}
        {...rest}
      >
        {loading ? (
          <LazySpinner show={loading} delay={delay}>
            <CircularProgress size='18px' color='var(--teal-100)' />
          </LazySpinner>
        ) : (
          label
        )}
      </a>
    )
  }

  return (
    <button
      type={type}
      ref={btnRef as RefObject<HTMLButtonElement>}
      className={clsx(
        commonClasses,
        'focus:outline-none focus:ring-2 focus:ring-jaa-teal-60'
      )}
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={accessibleLabel}
      onClick={(e) => {
        if (disabled || preventNavigation) e.preventDefault()
        if (onClick) onClick(e)
      }}
      {...rest}
    >
      {loading ? (
        <LazySpinner show={loading} delay={delay}>
          <CircularProgress size='18px' color='var(--teal-100)' />
        </LazySpinner>
      ) : (
        label
      )}
    </button>
  )
}

export default Button
